import React, { FC, ReactElement, SyntheticEvent, useContext, useEffect, useState } from 'react';
import { IGMBReview } from '../../../shared/interfaces/IGMBReview';
import styled from 'styled-components';
import { Box, Button, Checkbox, Tab, Tabs, Tooltip, Typography, useTheme } from '@mui/material';
import StarRating from '../../StarRating/StarRating';
import Helper from '../../../shared/helpers/Helper';
import { InfoOutlined, MarkChatRead, MoreVert, RateReview } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import TabPanel from '../../TabPanel/TabPanel';
import GMBReviewReply from '../ReviewReply/GMBReviewReply';
import { CommunityManagement } from '../../../shared/interfaces/ICommunityManagement';
import Note from '../../Notes/Note';
import DeleteReview from '../../Review/DeleteReview';
import { AuthContext } from '../../../providers/AuthProvider';
import { IGMBReviewComment, IGMBReviewEntry } from '../../../shared/interfaces/IGMBReviewEntry';
import { IReviewMetadata } from '../../../services/CommentAiService';
import { buildAiReviewDescriptor } from '../../../shared/helpers/AiReviewDescriptor';
import { IReviewNew } from '../../../shared/interfaces/IReviewNew';

const ReviewContainer = styled.div<{}>`
  padding: ${(props) => props.theme.spacing(1)};
  margin-bottom: ${(props) => props.theme.spacing(2)};
  border: 1px solid ${(props) => props.theme.palette.divider};
  border-radius: ${(props) => props.theme.shape.borderRadius}px;
  background-color: ${(props) => props.theme.palette.background.paper};
`;

const ReviewContent = styled.div`
  padding-top: ${(props) => props.theme.spacing(1)};
  padding-bottom: ${(props) => props.theme.spacing(1)};
  padding-left: ${(props) => props.theme.spacing(2)};
  padding-right: ${(props) => props.theme.spacing(2)};
  margin-bottom: ${(props) => props.theme.spacing(2)};
`;

const ReviewHeaderDiv = styled.div`
  display: flex;
  align-items: center;
  padding-right: ${(props) => props.theme.spacing(2)};
  margin-top: ${(props) => props.theme.spacing(1)};
`;

const ReviewFooterDiv = styled.div`
  display: flex;
  flex-direction: column;
`;

interface Props extends IGMBReview {
  rating: number;
  postgresLocationId: string;
  checked: boolean;
  onChange: (reviewId: string, selected: boolean) => void;
  communityManagementSettings: CommunityManagement;
  note: string;
  deletion: boolean;
  id: string;
  isUpdated: boolean;
  currentUpdatedStatus: boolean;
  reviewEntry: IGMBReviewEntry;
  onReviewCommentChange: (reviewEntryId: string, updates: Partial<IGMBReviewComment>) => void;
  subHeader?: ReactElement;
}

const GMBReview: FC<Props> = ({
  rating,
  id,
  comment,
  createTime,
  reviewId,
  reviewReply,
  reviewer,
  updateTime,
  checked,
  postgresLocationId,
  onChange,
  communityManagementSettings,
  note,
  deletion,
  currentUpdatedStatus,
  deleted,
  reviewEntry,
  onReviewCommentChange,
  subHeader,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { hasRole } = useContext(AuthContext);

  const [showFullText, setShowFullText] = useState(false);
  const [currentTab, setCurrentTab] = useState<number | boolean>(
    reviewEntry?.reviewComment?.expanded ? 0 : false
  );

  useEffect(() => {
    if (reviewEntry?.reviewComment?.expanded) {
      setCurrentTab(0);
    }
  }, [reviewEntry?.reviewComment?.expanded]);

  const handleTabChange = (event: SyntheticEvent, newValue: number | boolean) => {
    setCurrentTab(newValue);
  };

  const handleTabPanelClose = () => {
    setCurrentTab(false);
  };

  const reviewMeta: IReviewMetadata = {
    author: reviewer.displayName,
    score: rating,
    reviewerType: '',
    platform: reviewEntry.platform,
  };

  const descriptor = buildAiReviewDescriptor(reviewEntry as unknown as IReviewNew);

  reviewEntry.selectionMeta = {
    AIInput: descriptor.prompt,
    noReviewText: !descriptor.hasReviewText,
    communityManagement: communityManagementSettings,
    reviewMetaData: reviewMeta,
  };

  return (
    <ReviewContainer>
      <ReviewHeaderDiv>
        {onChange && (
          <Checkbox
            id={reviewId}
            checked={checked}
            onChange={(event) => onChange(reviewEntry.id, event.target.checked)}
            sx={{ mr: theme.spacing(1) }}
          />
        )}
        <StarRating
          value={rating}
          sx={{ mx: theme.spacing(2) }}
        />
        <Box sx={{ display: 'flex', ml: theme.spacing(1), cursor: 'pointer' }}>
          <Tooltip
            title={`${t('info_rating')}: ${rating}`}
            placement="top"
            arrow
          >
            <InfoOutlined
              fontSize="small"
              sx={{ color: theme.palette.primary.main }}
            />
          </Tooltip>
        </Box>
        <Tooltip
          title={
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography variant="body2">
                {t('written')} {Helper.getFormattedDate(createTime, false)}
              </Typography>
              {currentUpdatedStatus && (
                <Typography variant="body2">
                  {t('refreshed')} {Helper.getFormattedDate(updateTime, false)}
                </Typography>
              )}
            </Box>
          }
          arrow
        >
          <Typography
            variant="body2"
            sx={{ ml: 'auto', cursor: 'pointer' }}
          >
            {Helper.getFormattedDate(createTime, true)}
          </Typography>
        </Tooltip>
        {hasRole('can-use-note') ? (
          <>
            {(note || id) && (
              <Note
                note={note}
                id={id}
                type="gmbreview"
              />
            )}
          </>
        ) : null}
        {hasRole('can-use-deletion') ? (
          <>
            {(deletion || id) && (
              <DeleteReview
                id={id}
                deletion={deletion}
              />
            )}
          </>
        ) : null}
      </ReviewHeaderDiv>
      <>{subHeader && subHeader}</>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          my: theme.spacing(2),
          mx: theme.spacing(2),
        }}
      >
        <Box
          sx={{
            display: 'flex',
            mr: theme.spacing(1),
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <img
            src={reviewer.profilePhotoUrl}
            width="42"
            referrerPolicy="no-referrer"
            alt="Google avatar"
          />
        </Box>
        <Box>
          <Typography variant="body1">{reviewer.displayName}</Typography>
        </Box>
      </Box>
      {comment && (
        <ReviewContent>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography variant="body1">
              {showFullText ? comment : Helper.shortText(comment, 200)}
            </Typography>
            {comment.length > 200 && (
              <Box>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<MoreVert />}
                  onClick={() => setShowFullText((prevState) => !prevState)}
                  sx={{
                    my: theme.spacing(2),
                  }}
                >
                  {showFullText ? `${t('show_less')}` : `${t('show_more')}`}
                </Button>
              </Box>
            )}
          </Box>
        </ReviewContent>
      )}
      <ReviewFooterDiv>
        <Box sx={{ px: theme.spacing(2), width: '100%', boxSizing: 'border-box' }}>
          <Box sx={{ borderBottom: currentTab === false ? 0 : 1, borderColor: 'divider' }}>
            <Tabs
              value={currentTab}
              onChange={handleTabChange}
              aria-label={t('comment_actions')}
            >
              <Tab
                icon={reviewReply === null ? <RateReview /> : <MarkChatRead />}
                aria-label={t('comment')}
              />
            </Tabs>
          </Box>
          <TabPanel
            heading={`${t('comment')}`}
            value={currentTab}
            index={0}
            onCloseClick={handleTabPanelClose}
          >
            <GMBReviewReply
              id={reviewEntry.id}
              commentStatus={reviewEntry?.reviewComment?.status || ''}
              reviewerName={reviewer.displayName}
              comment={comment}
              locationId={postgresLocationId}
              reviewId={reviewId}
              reviewReply={reviewReply}
              communityManagementSettings={communityManagementSettings}
              input={descriptor.prompt}
              noReviewText={!descriptor.hasReviewText}
              reviewComment={reviewEntry.reviewComment}
              reviewMetadata={reviewMeta}
              onReviewCommentChange={onReviewCommentChange}
            />
          </TabPanel>
        </Box>
      </ReviewFooterDiv>
    </ReviewContainer>
  );
};

export default GMBReview;
